import { Component, OnInit } from '@angular/core';
import { ComponentBase } from 'src/app/main/dynamic/component-base';
import { WorkflowGatewayService } from 'src/app/service/workflow-gateway.service';
import { ExportService } from 'src/app/service/export.services';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthenticationService } from 'src/app/service/auth.service';
import { Router } from '@angular/router';
import { BackdropService } from 'src/app/service/backdrop.service';
import { SearchParamsModel } from 'src/app/model/search-params.model';
import { PagingModel } from 'src/app/model/paging.model';
import { ProcessSummaryModel } from 'src/app/model/process-summary.model';
import { PagingContainerModel } from 'src/app/model/paging-container.model';
import { AttributeModel } from 'src/app/main/dynamic/model/attribute.model';
import { DynamicEvent } from 'src/app/main/dynamic/dynamic.event';
import swal from 'sweetalert2';
import { environment } from 'src/environments/environment';

type PathType = 'policy' | 'type' | null;

@Component({
  selector: 'app-dynamic-process-list',
  templateUrl: './process-list.component.html',
  styleUrls: ['./process-list.component.scss'],
})
export class ProcessListDynamicComponent extends ComponentBase implements OnInit {
  pagingModel: PagingModel;
  processes: ProcessSummaryModel[] = [];
  totalColumns: number[] = [0, 1, 2];
  searchParams: SearchParamsModel = new SearchParamsModel();
  countPage = 1;
  currentPage = 1;
  totalPages: number | null = null;
  totalItems: number | null = null;
  isCountLoading = false;
  hasNextPage = false;
  hasPrevPage = false;
  isCatalogView = false;
  isLoading = false;
  filters: AttributeModel[] = [];
  showAdvancedSearch = false;
  showModalPrint = false;
  printProcessId: string;
  itemsAttr: AttributeModel[];
  selectedPrintItems: string[] = [];
  pageSize = 20;
  maxItems: number | null = null;
  private storageKey: string;
  private pathType: PathType = null;
  private pathId: string;
  private sourceUrl: string = null;

  constructor(
    private workflowService: WorkflowGatewayService,
    private exportService: ExportService,
    private spinner: NgxSpinnerService,
    private authService: AuthenticationService,
    private router: Router,
    private backdrop: BackdropService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeFromAttribute();
    this.initializeSearchParams();
    this.loadCountFromStorage();
    this.loadProcesses();
  }

  get hasFilters(): boolean {
    return this.filters && this.filters.length > 0;
  }

  get headerTitle(): string {
    return this.getOptionByKey(this.attribute, 'title', '');
  }

  get searchPlaceholder(): string {
    const placeholder = this.getOptionByKey(this.attribute, 'searchPlaceholder', '');
    return placeholder || 'Palavra chave...';
  }

  get showExportButton(): boolean {
    const optionValue = this.getOptionByKey(this.attribute, 'showExport', '');
    if (!optionValue) {
      return false;
    }
    return optionValue.toLowerCase() === 'true';
  }

  get exportLabel(): string {
    const label = this.getOptionByKey(this.attribute, 'exportLabel', '');
    return label || 'Exportar';
  }

  get rangeStart(): number {
    if (!this.pagingModel) {
      return 0;
    }
    const pageNumber = this.pagingModel.pageNumber || 1;
    const size = this.pagingModel.pageSize || this.pageSize;
    return (pageNumber - 1) * size + 1;
  }

  get rangeEnd(): number {
    if (!this.pagingModel) {
      return 0;
    }
    const pageNumber = this.pagingModel.pageNumber || 1;
    const size = this.pagingModel.pageSize || this.pageSize;
    return pageNumber * size;
  }

  get shouldShowPagination(): boolean {
    if (!this.pagingModel || this.isLoading) {
      return false;
    }
    return this.hasNextPage || this.hasPrevPage || this.currentPage > 1 || this.totalPages !== null;
  }

  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  handleFilterEmit(events: DynamicEvent[]): void {
    if (!events) {
      return;
    }
    events.forEach(event => {
      if (!event || !event.name) {
        return;
      }
      if (event.name.toLowerCase() === 'dosearch') {
        this.doSearch();
      }
    });
  }

  doSearchByName(): void {
    this.doSearch();
  }

  doSearch(): void {
    this.currentPage = 1;
    this.countPage = 1;
    this.totalItems = null;
    this.totalPages = null;
    this.hasNextPage = false;
    this.hasPrevPage = false;
    this.clearCountFromStorage();
    this.buildSearchFromFilters();
    this.loadProcesses(this.currentPage);
  }

  async doExport(): Promise<void> {
    this.buildSearchFromFilters();
    if (!this.searchParams.policyId && !this.searchParams.policyTypeId && this.processes.length > 0) {
      this.searchParams.policyId = this.processes[0].policyId;
    }

    if (this.searchParams.policyId || this.searchParams.policyTypeId) {
      this.spinner.show();
      await this.exportService.exportAsync(this.searchParams);
      this.spinner.hide();
    } else {
      swal.fire('Por favor', 'Selecione um tipo de cadastro para exportar.', 'warning');
    }
  }

  loadProcesses(pageNumber: number = 1, showLoading: boolean = true): void {
    if (showLoading) {
      this.spinner.show();
    }
    this.isLoading = true;
    this.processes = [];

    this.searchParams.orderDesc = false;

    this.workflowService
      .getSummary(pageNumber, this.pageSize, { searchParams: this.searchParams }, this.sourceUrl)
      .subscribe(
        (pagingContainer: PagingContainerModel) => {
          if (pagingContainer.items.length === 0 && pageNumber > 1) {
            this.mudarPagina(1, showLoading);
          } else {
            const limitedResult = this.applyMaxItemsLimit(pagingContainer, pageNumber, showLoading);
            if (limitedResult.redirectPage) {
              return;
            }
            this.processes = limitedResult.items;
            this.pagingModel = limitedResult.paging;
            const serverPageNumber = this.pagingModel?.pageNumber ?? this.currentPage ?? 1;
            this.currentPage = serverPageNumber;
            this.hasNextPage = (pagingContainer.links || []).some(l => l.rel === 'nextPage');
            this.hasPrevPage = (pagingContainer.links || []).some(l => l.rel === 'previousPage');
            this.countPage = Math.max(Math.floor((serverPageNumber - 1) / 15) * 15 + 1, 1);
            this.calculateTotalColumns();
            if (showLoading) {
              this.spinner.hide();
            }
            this.isLoading = false;
          }
        },
        (error) => this.handleHttpError(error)
      );
  }

  mudarPagina(pageNumber: number, showLoading: boolean = true): void {
    if (!pageNumber) {
      pageNumber = 1;
    }
    this.currentPage = pageNumber;
    this.loadProcesses(pageNumber, showLoading);
  }

  lastPages(): void {
    if (this.totalPages !== null) {
      const last = this.totalPages;
      const start = Math.max(last - 14, 1);
      this.currentPage = last;
      this.countPage = start;
      this.loadProcesses(this.currentPage, true);
    }
  }

  firstPages(): void {
    this.currentPage = 1;
    this.countPage = this.currentPage;
    this.loadProcesses(this.currentPage, true);
  }

  nextPages(): void {
    if (this.totalPages !== null) {
      const nextStart = this.countPage + 15;
      if (nextStart <= this.totalPages) {
        this.currentPage = nextStart;
        this.countPage = nextStart;
        this.loadProcesses(this.currentPage, true);
      }
    } else if (this.hasNextPage) {
      this.mudarPagina(this.currentPage + 1, true);
    }
  }

  previousPages(): void {
    if (this.countPage > 1) {
      const prevStart = Math.max(this.countPage - 15, 1);
      this.currentPage = prevStart;
      this.countPage = prevStart;
      this.loadProcesses(this.currentPage, true);
    }
  }

  obterPaginas(): number[] {
    const pageNumbers: number[] = [];
    if (this.pagingModel && this.totalPages !== null) {
      for (let index = this.countPage; index <= this.countPage + 14; index++) {
        if (index <= this.totalPages && index >= 1) {
          pageNumbers.push(index);
        }
      }
    }
    return pageNumbers;
  }

  isActive(page: number): boolean {
    return this.pagingModel ? page === this.pagingModel.pageNumber : false;
  }

  getColumnTitle(index: number): string {
    if (!this.processes || this.processes.length === 0) {
      return this.getDefaultTitle(index);
    }

    const firstItemAttrs = this.processes[0].attributes;

    if (!firstItemAttrs[index] || !firstItemAttrs[index].options) {
      return this.getDefaultTitle(index);
    }

    const titleOpt = firstItemAttrs[index].options.find(opt => opt.key.toLowerCase() === 'title');

    return titleOpt ? titleOpt.value : firstItemAttrs[index].key;
  }

  private getDefaultTitle(index: number): string {
    if (index === 0) {
      return 'Nome';
    } else if (index === 1) {
      return 'Tipo';
    }
    return 'Nome';
  }

  calculateTotalColumns(): void {
    if (this.processes && this.processes.length > 0) {
      const attributeCount = this.processes[0].attributes.length;
      this.totalColumns = Array.from({ length: attributeCount }, (_, i) => i);
    } else {
      this.totalColumns = [];
    }
  }

  onValueChange(event: any): void {
    if (!event?.target) {
      return;
    }
    if (event.target.checked) {
      this.selectedPrintItems.push(event.target.value);
    } else {
      const indexRemove = this.selectedPrintItems.indexOf(event.target.value);
      if (indexRemove >= 0) {
        this.selectedPrintItems.splice(indexRemove, 1);
      }
    }
  }

  sendPrint(): void {
    if (!this.selectedPrintItems || this.selectedPrintItems.length < 1) {
      swal.fire({
        title: 'Atenção',
        text: 'Favor selecionar um item para impressão.',
        icon: 'error',
        showCancelButton: false,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Fechar',
      });
    } else if (typeof window !== 'undefined') {
      window.open(
        `/print?processId=${this.printProcessId}&procedimentos=${this.selectedPrintItems.toString()}`,
        '_blank'
      );
      this.toggleModalPrint();
    }
  }

  toggleModalPrint(processId: string = null): void {
    if (processId) {
      this.printProcessId = processId;
      const processList = this.processes.find(p => p.id === processId);
      if (processList) {
        const itemsAttr = processList.attributes.find(attr => attr.type.toLowerCase() === 'items');
        this.itemsAttr = itemsAttr?.attributes || [];
        this.selectedPrintItems = this.itemsAttr
          .map(i => i.attributes?.[1]?.value)
          .filter((value): value is string => !!value);
      }
    }

    this.showModalPrint = !this.showModalPrint;

    if (this.showModalPrint) {
      this.backdrop.show();
    } else {
      this.backdrop.hide();
    }
  }

  loadCount(): void {
    if (this.isCountLoading) {
      return;
    }
    this.isCountLoading = true;
    this.workflowService
      .getCount(this.pageSize, { searchParams: this.searchParams }, this.sourceUrl)
      .subscribe(
        (countResult) => {
          this.totalItems = countResult.totalItems;
          this.totalPages = countResult.totalPages;
          this.isCountLoading = false;
          this.saveCountToStorage();
        },
        () => {
          this.isCountLoading = false;
        }
      );
  }

  updateList(): void {
    this.loadProcesses(this.currentPage, false);
  }

  trackFilter(index: number, filter: AttributeModel): string {
    return filter?.uid || filter?.key || `${index}`;
  }

  getPrintAttributeValue(itemAttr: AttributeModel, index: number): string {
    if (!itemAttr?.attributes || index < 0 || index >= itemAttr.attributes.length) {
      return '';
    }
    const value = itemAttr.attributes[index]?.value;
    return value ?? '';
  }

  private initializeFromAttribute(): void {
    this.pageSize = this.extractPageSize();
    this.maxItems = this.extractMaxItems();
    if (this.maxItems && this.maxItems > 0) {
      this.pageSize = Math.min(this.pageSize, this.maxItems);
    }
    this.filters = this.extractFilters();
    this.isCatalogView = this.extractResultType() === 'catalog';
    const pathValue = this.extractPath();
    this.applyPathToSearchParams(pathValue, this.searchParams);
    this.storageKey = this.buildStorageKey();
  }

  private initializeSearchParams(): void {
    const storedParams = this.loadSearchParamsFromStorage();
    if (storedParams) {
      this.searchParams = storedParams;
    }
    this.applyPathToSearchParams(this.extractPath(), this.searchParams);
    this.ensureSearchParamsArrays();
    this.hydrateFiltersFromSearchParams();
    this.buildSearchFromFilters(false);
  }

  private extractPath(): string {
    const pathAttr = this.getChildAttribute('path');
    if (pathAttr?.value) {
      return pathAttr.value;
    }
    const optionValue = this.getOptionByKey(this.attribute, 'path', '');
    return optionValue || null;
  }

  private extractPageSize(): number {
    const attr = this.getChildAttribute('pageSize');
    const optionValue = this.getOptionByKey(this.attribute, 'pageSize', '');
    const parsed = parseInt(attr?.value ?? optionValue, 10);
    const defaultSize = Math.max(environment.processPageSize || 0, 20);
    if (isNaN(parsed) || parsed <= 0) {
      return defaultSize;
    }
    return parsed;
  }

  private extractResultType(): string {
    const attr = this.getChildAttribute('resultType');
    if (attr?.value) {
      return attr.value.toLowerCase();
    }
    const opt = this.getOptionByKey(this.attribute, 'resultType', '');
    return opt?.toLowerCase() || 'list';
  }

  private extractMaxItems(): number | null {
    const attr = this.getChildAttribute('maxItems');
    const optionValue = this.getOptionByKey(this.attribute, 'maxItems', '');
    const parsed = parseInt(attr?.value ?? optionValue, 10);
    if (isNaN(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }

  private extractFilters(): AttributeModel[] {
    const filtersAttr = this.getChildAttribute('filters');
    if (!filtersAttr || !filtersAttr.attributes) {
      return [];
    }
    return filtersAttr.attributes.map(attr => this.cloneAttribute(attr));
  }

  private cloneAttribute(attribute: AttributeModel): AttributeModel {
    if (!attribute) {
      return null;
    }
    return {
      ...attribute,
      options: attribute.options ? attribute.options.map(opt => ({ ...opt })) : [],
      attributes: attribute.attributes ? attribute.attributes.map(attr => this.cloneAttribute(attr)) : [],
      postEvents: attribute.postEvents ? attribute.postEvents.map(attr => this.cloneAttribute(attr)) : [],
    };
  }

  private applyPathToSearchParams(path: string | null, searchParams: SearchParamsModel): void {
    if (!path) {
      this.pathType = null;
      this.pathId = null;
      return;
    }
    const match = path.match(/(policy|type)\/(.+)/i);
    if (match) {
      this.pathType = match[1].toLowerCase() as PathType;
      this.pathId = match[2];
    } else {
      this.pathType = null;
      this.pathId = null;
    }

    if (this.pathType === 'policy') {
      searchParams.policyId = this.pathId;
      searchParams.policyTypeId = null;
    } else if (this.pathType === 'type') {
      searchParams.policyTypeId = this.pathId;
      searchParams.policyId = null;
    }
  }

  private buildStorageKey(): string | null {
    if (!this.pathType || !this.pathId) {
      return null;
    }
    return `searchParams_${this.pathType}_${this.pathId}`;
  }

  private get countStorageKey(): string | null {
    if (!this.storageKey) { return null; }
    return this.storageKey.replace('searchParams_', 'processCount_');
  }

  private loadCountFromStorage(): void {
    const key = this.countStorageKey;
    if (!key) { return; }
    const stored = sessionStorage.getItem(key);
    if (!stored) { return; }
    try {
      const count = JSON.parse(stored);
      this.totalItems = count.totalItems ?? null;
      this.totalPages = count.totalPages ?? null;
    } catch {
      this.totalItems = null;
      this.totalPages = null;
    }
  }

  private saveCountToStorage(): void {
    if (this.totalItems === null) { return; }
    const key = this.countStorageKey;
    if (!key) { return; }
    sessionStorage.setItem(key, JSON.stringify({
      totalItems: this.totalItems,
      totalPages: this.totalPages
    }));
  }

  private clearCountFromStorage(): void {
    const key = this.countStorageKey;
    if (key) { sessionStorage.removeItem(key); }
  }

  private loadSearchParamsFromStorage(): SearchParamsModel | null {
    if (!this.storageKey) {
      return null;
    }
    const stored = sessionStorage.getItem(this.storageKey);
    if (!stored) {
      return null;
    }
    try {
      const parsed = JSON.parse(stored);
      const model = new SearchParamsModel();
      Object.assign(model, parsed);
      if (!model.attributes) {
        model.attributes = [];
      }
      return model;
    } catch {
      return null;
    }
  }

  private ensureSearchParamsArrays(): void {
    if (!this.searchParams.attributes) {
      this.searchParams.attributes = [];
    }
  }

  private hydrateFiltersFromSearchParams(): void {
    if (!this.filters || this.filters.length === 0 || !this.searchParams) {
      return;
    }

    Object.keys(this.searchParams).forEach(key => {
      const value = (this.searchParams as any)[key];
      if (Array.isArray(value)) {
        return;
      }
      if (value !== null && value !== undefined && value !== '') {
        this.setFilterValueByFieldName(key, value);
      }
    });

    if (this.searchParams.attributes) {
      this.searchParams.attributes.forEach(attr => this.setFilterFromAttribute(attr));
    }
  }

  private setFilterValueByFieldName(fieldName: string, value: any): void {
    const filter = this.filters.find(f =>
      (f.options || []).some(opt => opt.key === 'searchField' && opt.value === fieldName)
    );

    if (!filter) {
      return;
    }

    const binding = this.getBindingAttribute(filter);
    if (!binding) {
      filter.value = value;
      this.showAdvancedSearch = true;
      return;
    }

    if (this.isValueInBindingList(binding, value)) {
      filter.value = value;
      this.showAdvancedSearch = true;
    }
  }

  private setFilterFromAttribute(attribute: AttributeModel): void {
    const filter = this.filters.find(f => f.key === attribute.key);
    if (filter) {
      filter.value = attribute.value;
      this.showAdvancedSearch = true;
    }
  }

  private buildSearchFromFilters(persist: boolean = true): void {
    this.ensureSearchParamsArrays();
    this.searchParams.attributes = [];
    if (!this.filters) {
      if (persist) {
        this.persistSearchParams();
      }
      return;
    }

    this.filters.forEach(filter => this.buildSearchParamsForFilter(filter));
    if (persist) {
      this.persistSearchParams();
    }
  }

  private buildSearchParamsForFilter(filter: AttributeModel): void {
    if (!filter || !filter.attributes) {
      return;
    }

    const filterPath = filter.attributes.find(attr => attr.type?.toLowerCase() === 'filterpath');
    if (!filterPath) {
      return;
    }

    const searchField = filter.options?.find(opt => opt.key === 'searchField');
    const binding = this.getBindingAttribute(filter);
    const bindingOptions = binding?.attributes || [];
    const selectedOptionMeta = filter.attributes.find(attr => attr.type?.toLowerCase() === 'filterselectedoption');

    let selectedKey = filter.value;
    if (selectedKey === null || selectedKey === undefined || selectedKey === '') {
      selectedKey = null;
    } else if (selectedOptionMeta && selectedOptionMeta.attributes?.length) {
      selectedKey = selectedOptionMeta.attributes[0].key || null;
    } else if (bindingOptions.length) {
      const selected = bindingOptions.find(o => o.key === filter.value) ||
        bindingOptions.find(o => (o.value || '').toLowerCase() === String(filter.value).toLowerCase());
      selectedKey = selected ? selected.key : filter.value;
    }

    if (filterPath.attributes) {
      filterPath.attributes.forEach(o => o.value = selectedKey);
    }

    if (searchField) {
      (this.searchParams as any)[searchField.value] = selectedKey ?? '';
    } else {
      this.searchParams.attributes.push(filter);
    }
  }

  private getBindingAttribute(filter: AttributeModel): AttributeModel {
    if (!filter?.attributes) {
      return null;
    }
    return filter.attributes.find(attr =>
      (attr.key && attr.key.toLowerCase() === 'binding') ||
      (attr.type && attr.type.toLowerCase() === 'binding')
    );
  }

  private isValueInBindingList(binding: AttributeModel, value: any): boolean {
    if (!binding?.attributes) {
      return false;
    }

    return binding.attributes.some(opt =>
      (opt.value || '').toString().toLowerCase() === (value || '').toString().toLowerCase()
    );
  }

  private persistSearchParams(): void {
    if (!this.storageKey) {
      return;
    }
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.searchParams));
    } catch {}
  }

  private getChildAttribute(key: string): AttributeModel {
    if (!this.attribute?.attributes || !key) {
      return null;
    }
    return this.attribute.attributes.find(attr => attr.key?.toLowerCase() === key.toLowerCase());
  }

  private handleHttpError(error: any): void {
    this.spinner.hide();
    this.isLoading = false;

    if (error.status === 401) {
      this.authService.logout();
      this.router.navigate(['login']);
    } else if (error.status === 403) {
      swal.fire('Atenção', 'Este usuário não tem permissão para acessar este conteúdo.')
        .then(() => this.router.navigate(['']).then(() => swal.close()));
    } else if (error.message) {
      swal.fire('Atenção', error.message, 'error').then(() => {
        if (error.goBackToHome) {
          this.router.navigate(['']).then(() => swal.close());
        }
      });
    } else {
      swal.fire('Atenção', 'Algo errado aconteceu; Por favor, tente novamente.', 'error');
    }
  }

  private getPaginationThreshold(): number {
    return Math.max(this.pageSize, 20);
  }

  private applyMaxItemsLimit(
    pagingContainer: PagingContainerModel,
    pageNumber: number,
    showLoading: boolean
  ): {
    items: ProcessSummaryModel[];
    paging: PagingModel;
    redirectPage?: number;
  } {
    if (!this.maxItems || this.maxItems <= 0) {
      return {
        items: pagingContainer.items,
        paging: pagingContainer.paging,
      };
    }

    const originalPaging = pagingContainer.paging || ({} as PagingModel);
    const originalTotalItems = this.totalItems ?? originalPaging.totalItems ?? pagingContainer.items.length;
    const limitedTotalItems = Math.min(originalTotalItems, this.maxItems);
    const effectiveTotalPages =
      limitedTotalItems > 0 ? Math.ceil(limitedTotalItems / this.pageSize) : 0;

    const startIndex = (pageNumber - 1) * this.pageSize;

    if (startIndex >= limitedTotalItems && effectiveTotalPages >= 0) {
      const targetPage = Math.max(effectiveTotalPages, 1);
      if (pageNumber !== targetPage) {
        this.mudarPagina(targetPage, showLoading);
        return { items: [], paging: originalPaging, redirectPage: targetPage };
      }
    }

    const remaining = Math.max(limitedTotalItems - startIndex, 0);
    const allowedCount = Math.min(this.pageSize, remaining, pagingContainer.items.length);
    const limitedItems = pagingContainer.items.slice(0, allowedCount);

    const updatedPaging: PagingModel = {
      ...originalPaging,
      totalItems: limitedTotalItems,
      totalPages: effectiveTotalPages,
      pageNumber,
      pageSize: this.pageSize,
    };

    return {
      items: limitedItems,
      paging: updatedPaging,
    };
  }
}

